import {
	Attachment,
	ObjectAvailability,
	Translations,
	UUID,
} from '@/types/types';
import { ApiQueryKey } from '@/configs/apiQueryKey';
import { Asset } from './AssetApi';
import { ProductModuleCollection } from './ProductModuleCollectionApi';
import { PriceGroup } from './PriceGroupApi';
import { Store } from '@/api/StoreApi';
import { AttributeValue } from './AttributeValueApi';
import EntityApi, { BaseEntity } from './EntityApi';
import httpClient from './httpClient';

export enum ProductModuleType {
	LINE = 'LINE',
	LEFT = 'LEFT',
	RIGHT = 'RIGHT',
	ACCESSORY = 'ACCESSORY',
}

export interface ProductModuleStoreConfiguration {
	store: Store;
	name: string;
	description: string;
	dimensions: string;
	listOrder: number;
	price: number;
	taxRate: number;
	status: ObjectAvailability;
	additionalCollections: ProductModuleCollection[];
	productModulePriceGroups: ProductModulePriceGroup[];
}

export interface ProductModulePriceGroup extends BaseEntity {
	priceGroup: PriceGroup;
	price: number;
}

export interface ProductModule extends BaseEntity {
	code: string;
	image?: Attachment;
	productModuleCollection: ProductModuleCollection;
	asset: Asset;
	listOrder: number;
	moduleType: ProductModuleType;
	storeConfigurations: ProductModuleStoreConfiguration[];
	productModulePriceGroups: ProductModulePriceGroup[];
	modulesBlacklist?: ProductModule[];
	modulesBlacklistIds: UUID[];
	modulesWhitelist?: ProductModule[];
	modulesWhitelistIds: UUID[];
	hasValuesOverride: boolean;
	attributeValueOverrides?: AttributeValue[];
	attributeValueOverridesIds: UUID[];
	translations: Translations<{
		name: string;
		description: string;
	}>;
}

export enum PriceSyncStoreStatus {
	SYNCED = 'synced',
	SKIPPED = 'skipped',
	ERROR = 'error',
}

export enum PriceSyncItemStatus {
	UPDATED = 'updated',
	UNCHANGED = 'unchanged',
	NOT_FOUND = 'not_found',
	SKIPPED = 'skipped',
}

export interface SyncPricesResponse {
	productModuleId: UUID;
	code: string;
	stores: {
		storeId: UUID;
		storeName: string;
		status: PriceSyncStoreStatus;
		reason: string;
		items: {
			requestedProductNumber: string;
			oldPrice: number;
			status: PriceSyncItemStatus;
			priceGroupId: UUID;
			priceGroupCode: string;
			productNumber: string;
			exactMatch: boolean;
			newPrice: number;
		}[];
	}[];
}

class ProductModuleApi extends EntityApi<ProductModule> {
	constructor() {
		super(ApiQueryKey.productModules);
		this.updatePriceGroups = this.updatePriceGroups.bind(this);
		this.syncPrices = this.syncPrices.bind(this);
	}

	async updatePriceGroups(data: [ProductModule, ProductModulePriceGroup[]][]) {
		const requests: Promise<ProductModule>[] = [];

		for (const [productModule, productModulePriceGroups] of data) {
			requests.push(
				super.update({
					id: productModule.id,
					productModulePriceGroups,
				})
			);
		}

		await Promise.all(requests);
	}

	async syncPrices(id: UUID) {
		const response = await httpClient.post<SyncPricesResponse>(
			`${this.queryKey}/${id}/sync-prices`
		);

		return response.data;
	}
}

export default new ProductModuleApi();
